import { KeyboardEvent, ReactNode } from 'react';
import { MoneyDisplay } from '../MoneyDisplay';
import styles from './SummaryCard.module.css';

export type Status = 'success' | 'warning' | 'danger' | 'info' | 'neutral';
/** density: 'comfortable' (ברירת מחדל, dashboard) | 'compact' (~50% — צפיפות גבוהה במסכים עם הרבה cards) */
export type Density = 'comfortable' | 'compact';

export interface SummaryCardTrend {
  /** אחוז שינוי (חיובי → ↑, שלילי → ↓, 0 → →) */
  value: number;
  /** תיאור תקופה (למשל "מהחודש שעבר") */
  period: string;
}

export interface SummaryCardProps {
  /** כותרת ה-metric (eyebrow, uppercase) */
  title: string;
  /** ערך — `number` → MoneyDisplay, `string` → טקסט mono */
  value: number | string;
  /** דריסה של ברירת המחדל ל-value */
  valueFormatter?: (v: number | string) => ReactNode;
  /** תיאור משני מתחת ל-value */
  subtitle?: string;
  /** מגמה עם תקופה */
  trend?: SummaryCardTrend;
  /** גוון: success/warning/danger/info/neutral */
  status?: Status;
  /** צפיפות: comfortable (ברירת מחדל, large value) או compact (~50%, default value, padding קטן) */
  density?: Density;
  /** אם קיים, הכרטיס clickable + role=button + drill-arrow */
  onClick?: () => void;
  /** aria-label עבור onClick (ברירת מחדל: title) */
  drillDownLabel?: string;
  /** skeleton placeholder */
  loading?: boolean;
  /** הודעת שגיאה — תוצג במקום value */
  error?: string;
  /** class נוסף ל-root */
  className?: string;
}

const cx = (...classes: Array<string | false | null | undefined>): string =>
  classes.filter(Boolean).join(' ');

const TREND_ARROW = (delta: number): string => (delta > 0 ? '↑' : delta < 0 ? '↓' : '→');

/**
 * כרטיס metric אחיד עם title + value + subtitle + trend, אופציונלי clickable ו-status.
 *
 * **8+ מופעים ב-dashboard** — composition pattern: כש-`value` הוא `number` משתמש ב-`<MoneyDisplay />`.
 * R3 — clickable per metric (drill-down).
 *
 * @example
 * <SummaryCard title="רווח נקי" value={125430} status="success" />
 *
 * @example
 * <SummaryCard
 *   title="הכנסות"
 *   value={89500}
 *   trend={{ value: 12.5, period: 'מהחודש שעבר' }}
 *   onClick={() => openDrillDown()}
 *   drillDownLabel="פתח פירוט הכנסות"
 * />
 */
export const SummaryCard = ({
  title,
  value,
  valueFormatter,
  subtitle,
  trend,
  status = 'neutral',
  density = 'comfortable',
  onClick,
  drillDownLabel,
  loading = false,
  error,
  className,
}: SummaryCardProps): JSX.Element => {
  const isClickable = typeof onClick === 'function' && !loading && !error;
  // density compact משפיע על padding/variant/min-width — ראה SummaryCard.module.css
  const densityClass = density === 'compact' ? styles.compact : styles.comfortable;
  // ב-compact: MoneyDisplay variant=default (text-base, ~14-16px) במקום large (text-3xl, ~30px)
  const moneyVariant: 'large' | 'default' = density === 'compact' ? 'default' : 'large';

  // ─── Loading state — skeleton (קודם ל-status כי loading דורש placeholder) ───
  if (loading) {
    return (
      <div
        className={cx(styles.root, styles[status], densityClass, styles.loading, className)}
        data-testid="summary-card"
        aria-busy="true"
        aria-live="polite"
      >
        <div className={cx(styles.skeleton, styles.skeletonTitle)} />
        <div className={cx(styles.skeleton, styles.skeletonValue)} />
        <div className={cx(styles.skeleton, styles.skeletonSubtitle)} />
      </div>
    );
  }

  // ─── Value renderer — composition עם MoneyDisplay למספרים ───
  const renderValue = (): ReactNode => {
    if (valueFormatter) {
      return valueFormatter(value);
    }
    if (typeof value === 'number') {
      return <MoneyDisplay amount={value} variant={moneyVariant} />;
    }
    return <span className={styles.valueString}>{value}</span>;
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (!isClickable || !onClick) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      className={cx(
        styles.root,
        styles[status],
        densityClass,
        isClickable && styles.clickable,
        className
      )}
      data-testid="summary-card"
      onClick={isClickable ? onClick : undefined}
      onKeyDown={handleKeyDown}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      aria-label={isClickable ? drillDownLabel || title : undefined}
      title={isClickable ? drillDownLabel : undefined}
    >
      <div className={styles.title}>{title}</div>
      <div className={styles.valueWrap}>
        {error ? (
          <span
            className={styles.error}
            data-testid="summary-card-error"
            role="alert"
          >
            <span aria-hidden="true">⚠</span> {error}
          </span>
        ) : (
          renderValue()
        )}
      </div>
      {subtitle && !error && (
        <div className={styles.subtitle}>{subtitle}</div>
      )}
      {trend && !error && (
        <div className={styles.trendBlock} data-testid="summary-card-trend">
          <span
            className={cx(
              styles.trendArrow,
              trend.value > 0 && styles.trendUp,
              trend.value < 0 && styles.trendDown,
              trend.value === 0 && styles.trendFlat
            )}
            aria-hidden="true"
          >
            {TREND_ARROW(trend.value)}
          </span>
          <span className={styles.trendValue}>
            {trend.value > 0 ? '+' : ''}
            {trend.value}%
          </span>
          <span className={styles.trendPeriod}>{trend.period}</span>
        </div>
      )}
      {isClickable && (
        <span
          className={styles.drillIndicator}
          aria-hidden="true"
          data-testid="summary-card-drill"
        >
          ←
        </span>
      )}
    </div>
  );
};
